<script setup lang="ts">
import { onBeforeMount, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useStorage } from '@vueuse/core'
import { add, isAfter } from 'date-fns'

import ErrorBoundary from '@/components/app/ErrorBoundary/ErrorTemplate.vue'

import useCdcProps from '@/composables/useCdcProps'
import usePurchasedProducts from '@/composables/usePurchasedProducts'
import useEventSource from '@/composables/useEventSource'
import useService from '@/composables/useServices'
import usePopUp from '@/composables/usePopUp'

import useContextStore from '@/stores/context'
import useModulesStore from '@/stores/modules'
import useProductsStore from '@/stores/products'
import useLayoutStore from '@/stores/layout'

const { locale } = useI18n()
const { context, xprops } = useCdcProps()
const { fetchPurchasedModules } = usePurchasedProducts()
const { openConnection } = useEventSource()
const { handlePopUpLauncher } = usePopUp()
const route = useRoute()

const contextStore = useContextStore()
const modulesStore = useModulesStore()
const productsStore = useProductsStore()
useLayoutStore()

import { InstalledModule } from '@/types/global.interfaces'

onBeforeMount(() => {
  contextStore.setContext(context)
  modulesStore.setAllInstalled(context.installed_modules)
  productsStore.setStateInBatch(
    context.installed_modules.map((module: InstalledModule) => ({
      configUrl: module.config_url,
      installedVersion: module.version,
      name: module.name,
      status: module.status,
    })),
  ),
    xprops.export({
      getStore: () => {
        return {
          context: contextStore,
          modules: productsStore,
        }
      },
    })

  locale.value = contextStore.isoLang as string

  handleOpenEventSourceConnection()
  setIsPse()

  if (!route.meta.skipFetchPurchasedProduct) {
    handleFetchPurchasedModule()
  }

  if (!route.meta.skipFetchPsAccount) {
    getPsAccount()
  }
})

onMounted(async () => {
  await new Promise((resolve) => setTimeout(resolve, 2000))
  handlePopUpLauncher()
})

// To keep modules in good status
// when the user navigates using the browser's history button.
watch(
  () => route.name,
  () => {
    handleFetchPurchasedModule()
  },
)

function handleFetchPurchasedModule() {
  if (
    [
      'dashboard-tips-and-updates',
      'home',
      'modules',
      'modules-manager-message',
      'modules-scope',
      'module',
      'module-modal',
      'module-medias',
      'recommendations',
    ].includes(route.name as string)
  ) {
    fetchPurchasedModules({
      forceApi: ['home', 'modules-manager-message', 'dashboard-tips-and-updates'].includes(
        route.name as string,
      ),
    })
  }
}

function handleOpenEventSourceConnection() {
  if (
    isAuthorisedRouteForSSE(route.name as string) &&
    contextStore.accountsToken
  ) {
    openConnection()
  }

  function isAuthorisedRouteForSSE(routeName: string): boolean {
    return [
      'home',
      'modules',
      'modules-scope',
      'module',
      'module-modal',
      'module-medias',
    ].includes(routeName)
  }
}

function setIsPse() {
  if (
    context.installed_modules.some(
      (module: any) =>
        module.name === 'ps_enterprise_module' &&
        (module.status === 'enabled__mobile_enabled' ||
          module.status === 'enabled__mobile_disabled'),
    )
  ) {
    contextStore.enablePsEntreprise()
  }
}

async function getPsAccount() {
  const { productService } = useService()

  const defaultValue: any = null
  const PS_ACCOUNT_ID = 49648
  const storage = useStorage(`ps_accounts-module-${xprops.context.shop_url}`, {
    expiredAt: new Date(),
    ps_accounts: defaultValue,
  })

  if (
    !storage.value.ps_accounts?.version ||
    isAfter(new Date(), new Date(storage.value.expiredAt))
  ) {
    if (
      !productsStore.all['ps_accounts']?.technical_info?.last_release
        ?.product_version
    ) {
      const psMboModuleFromApi = await productService.fetch(PS_ACCOUNT_ID)
      productsStore.setOne(psMboModuleFromApi)
    }

    storage.value.ps_accounts = productsStore.all['ps_accounts']
    storage.value.expiredAt = add(new Date(), { hours: 2 })
  } else {
    delete storage.value.ps_accounts.installedVersion
    delete storage.value.ps_accounts.status
    productsStore.setOne(storage.value.ps_accounts)
  }
}
</script>

<template>
  <ErrorBoundary>
    <slot></slot>
  </ErrorBoundary>
</template>
